import { csrfToken } from "./csrf";

// お喋り部屋 (Spaces 風) の WebRTC クライアント。
//
// トポロジ: スター型 (host ↔ N listeners)。接続構造は「host が各 listener と
// 1 本ずつ RTCPeerConnection を張る」従来のままだが、音声はホストが WebAudio で
// 全員の声をミックスして配り直す (mix-minus) ことで「全員が互いに話す」
// グループ通話になっている。リスナー間の声は host 経由の 2 ホップ。
// DM 音声通話と同じく HTTP long-poll 方式のシグナリング
// (/oshaberi/{room}/signal/{action}?wait=N)。SFU は使わない。
// サーバはメッセージ到着の瞬間に poll を返すため、join → offer → answer →
// ICE の往復がポーリング間隔に律速されず、参加が速い。
//
// 流れ:
//   host:
//     1. getUserMedia({audio:true}) → localStream
//     2. /signal/poll で "peer_joined" を受け取るたびに、新リスナー宛に
//        RTCPeerConnection を作り、mix-minus 出力を addTrack → createOffer → POST offer
//     3. /signal/poll で "answer" / "ice" を受け取って各 PC に流す
//     4. ontrack で受けたリスナー音声をローカル再生しつつミキサーへ登録
//   listener:
//     1. /signal/poll で host からの "offer" を受け取ったら
//        RTCPeerConnection を作り、setRemoteDescription → createAnswer → POST answer
//     2. 受信した track (ホスト + 他リスナーの合成音声) を <audio> で再生
//     3. ミュート解除すると自分の声が host のミキサー経由で全員に届く
//   両方:
//     - "ice" を相手から受けたら addIceCandidate
//     - "peer_left" で当該 PC を close
//     - "room_ended" で全部閉じて一覧へ戻る

interface IceServerCreds {
  iceServers: RTCIceServer[];
  expires_at?: number;
}

interface SignalMsg {
  kind: "offer" | "answer" | "ice" | "peer_joined" | "peer_left" | "room_ended" | "reaction";
  from?: number;
  peer?: number;
  sdp?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
  emoji?: string;
}

// /oshaberi/{id}/state の参加者 1 人分 (server: handler/oshaberi.go statePart)
interface StatePart {
  user_id: number;
  handle: string;
  name: string;
  avatar_url: string;
  role: string;
  is_muted: boolean;
}

interface StateResp {
  status?: string;
  listener_count?: number;
  participants?: StatePart[];
}

interface RoomDataset {
  roomId: number;
  hostId: number;
  myId: number;
  status: string;
  iamHost: boolean;
  iamJoined: boolean;
}

// long-poll の待機秒: サーバはメッセージ到着まで最大この秒数ブロックして返す。
// 未接続リスナーは hello 再要求 (offer の再送依頼) を回すため短め、それ以外は
// 長め (サーバ WriteTimeout 30s の内側で 25 秒上限)。
const LONGPOLL_WAIT_S = 25;
const LONGPOLL_WAIT_JOINING_S = 3;
const STATE_INTERVAL_MS = 5000;

export function initOshaberiRoom(): void {
  const el = document.getElementById("oshaberiRoom") as HTMLDivElement | null;
  if (!el) return;

  const ds = readDataset(el);
  if (ds.status !== "live") return;       // 終了済み: 何もしない

  // ここから下は live 部屋の全閲覧者 (未参加含む) 共通の UI:
  // 経過時間タイマー / 参加者グリッドのライブ更新 / 共有ボタン。
  startElapsedTimer(el);
  startStateUpdater(ds);
  bindShareButton(el);

  if (!ds.iamJoined) return;              // まだ参加していない: 音声接続はしない (フォーム送信を待つ)

  const room = new OshaberiClient(ds);
  bindReactions(ds);
  const enableBtn = document.getElementById("oshaberiEnable") as HTMLButtonElement | null;

  if (ds.iamHost) {
    // ホスト: マイクは必ずユーザー操作 (タップ) で取得する。iOS/PWA は
    // ジェスチャー無しの getUserMedia をブロックするため、自動起動だと無音になる。
    setStatus("「通話を開始」をタップしてください");
    if (enableBtn) {
      enableBtn.hidden = false;
      enableBtn.addEventListener("click", () => {
        enableBtn.disabled = true;
        room
          .startHost()
          .then(() => { enableBtn.hidden = true; })
          .catch((err) => {
            enableBtn.disabled = false;
            room.resetStarted(); // マイク拒否はリトライ可能に
            setStatus("マイクが使えません: " + (err && err.message ? err.message : "拒否されました"));
          });
      });
    } else {
      room.startHost().catch((e) => console.warn("[oshaberi] host start", e));
    }
  } else {
    // リスナー: DM 通話と同じく「ユーザのタップ → getUserMedia → シグナリング」。
    // 受信専用 answer は iOS Safari で無音になる既知の地雷のため、リスナーも
    // マイクトラックを取得して双方向 SDP にする (取得直後にミュート = 声は送らない)。
    setStatus("「音声をオンにする」をタップしてください");
    if (enableBtn) {
      enableBtn.hidden = false;
      enableBtn.addEventListener("click", () => {
        enableBtn.disabled = true;
        room
          .startListener()
          .then(() => { enableBtn.hidden = true; room.enableAudio(); })
          .catch(() => {
            // マイク拒否でも受信専用で続行 (Android/PC はこれで聴ける)
            enableBtn.hidden = true;
            room.enableAudio();
          });
      });
    } else {
      room.startListener().catch((e) => console.warn("[oshaberi] listener start", e));
    }
  }

  // 出力切替 (スピーカー ↔ 受話口)。リスナー側のみ意味があるが、ボタンが
  // あればバインドする (テンプレ側でリスナーにのみ描画)。
  const outBtn = document.getElementById("oshaberiOutputBtn") as HTMLButtonElement | null;
  if (outBtn) {
    outBtn.addEventListener("click", () => { void room.toggleEarpiece(outBtn); });
  }

  // ページ離脱で必ず後始末
  window.addEventListener("beforeunload", () => room.stop());
}

function readDataset(el: HTMLElement): RoomDataset {
  return {
    roomId: parseInt(el.dataset.roomId || "0", 10),
    hostId: parseInt(el.dataset.hostId || "0", 10),
    myId: parseInt(el.dataset.myId || "0", 10),
    status: el.dataset.status || "",
    iamHost: el.dataset.iamHost === "1",
    iamJoined: el.dataset.iamJoined === "1",
  };
}

function setStatus(s: string): void {
  const st = document.getElementById("oshaberiStatus");
  if (st) st.textContent = s;
}

// 画面に出る診断ログ (原因切り分け用)。#oshaberiDebug があれば追記する。
function dbg(s: string): void {
  try {
    // eslint-disable-next-line no-console
    console.log("[oshaberi]", s);
  } catch (_e) { /* noop */ }
}

function urlPrefix(): string {
  return (window as unknown as { V2_URL_PREFIX?: string }).V2_URL_PREFIX || "";
}

class OshaberiClient {
  private ds: RoomDataset;
  private localStream: MediaStream | null = null;
  // peerID (= 自分から見た「相手」の user_id) → RTCPeerConnection
  private pcs: Map<number, RTCPeerConnection> = new Map();
  private pendingIce: Map<number, RTCIceCandidateInit[]> = new Map();
  private iceServers: RTCIceServer[] = [
    { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
  ];
  private polling = false; // long-poll ループ起動済みか
  private pollAbort: AbortController | null = null; // 待機モード切替 / 停止用
  private anyConnected = false; // どれかの PC が connected になったか (hello 再要求の停止条件)
  private stopped = false;
  private audioCtx: AudioContext | null = null;
  // === mix-minus ミキサー (ホストのみ使用) ===
  private micSource: MediaStreamAudioSourceNode | null = null;
  private peerSources: Map<number, MediaStreamAudioSourceNode> = new Map(); // 受信音声 → source
  private peerDests: Map<number, MediaStreamAudioDestinationNode> = new Map(); // 宛先別の合成出力
  // seatUid → その席の音量メーターを止める関数。再接続で張り直す時に旧ループを
  // 確実に殺す (でないと RAF ループと AnalyserNode が接続のたびに増殖する)。
  private meterStops: Map<number, () => void> = new Map();

  // startHost/startListener が一度走ったか。audio.play() reject 後にボタンを
  // 再表示した際、再タップで開始処理を二重実行しない (マイク二重取得 +
  // ミュートリスナー二重登録の防止)。再タップは enableAudio() だけ行う。
  private started = false;

  constructor(ds: RoomDataset) {
    this.ds = ds;
  }

  resetStarted(): void { this.started = false; }

  // ホスト: ユーザー操作 (タップ) から呼ぶ。getUserMedia → 通話開始。
  async startHost(): Promise<void> {
    if (this.started) { this.enableAudio(); return; }
    this.started = true;
    dbg("host: 通話開始 (ICE取得→マイク)");
    await this.fetchIceServers().catch(() => {});
    setStatus("マイクを準備中…");
    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    dbg("host: マイクOK tracks=" + this.localStream.getAudioTracks().length);
    setStatus("🔴 通話を開始しました — 参加者を待っています");
    this.attachLevelMeter(this.localStream, this.ds.myId); // 自分のマイク音量を可視化
    this.bindMuteButton();
    // 既存の listener (リロード後の合流) にも offer を送る。
    this.bootstrapExistingPeers().catch((e) => console.warn("[oshaberi] bootstrap peers", e));
    this.startPolling();
  }

  // リスナー: タップから呼ぶ (DM 通話の応答側と同じ流れ)。
  // マイクを取得して即ミュート (声は送らない) → 双方向 SDP で answer する。
  async startListener(): Promise<void> {
    if (this.started) { this.enableAudio(); return; }
    this.started = true;
    dbg("listener: 開始 (ICE取得→マイク→pollでoffer待ち)");
    await this.fetchIceServers().catch(() => {});
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.localStream.getAudioTracks().forEach((t) => { t.enabled = false; });
      dbg("listener: マイクOK (ミュート済)");
    } catch (e) {
      dbg("listener: マイク不可 → 受信専用で続行");
      this.localStream = null;
      // ここでは throw しない: 受信専用でも開始する
    }
    setStatus("接続中… 音声を待っています");
    this.bindMuteButton(); // リスナーもミュート解除で発言できる (声は host のミキサー経由で全員に届く)
    this.startPolling();
  }

  // スピーカー ↔ 受話口 (電話モード) の切替。
  // Android Chrome 系: setSinkId + enumerateDevices で受話口デバイスがあれば切替。
  // iOS Safari: OS が出力ルーティング API を公開していないため切替不可 (告知する)。
  async toggleEarpiece(btn: HTMLButtonElement): Promise<void> {
    const audio = document.getElementById("oshaberiRemoteAudio") as (HTMLAudioElement & {
      setSinkId?: (id: string) => Promise<void>;
      sinkId?: string;
    }) | null;
    if (!audio || typeof audio.setSinkId !== "function") {
      setStatus("この端末のブラウザは出力切替に未対応です (iOS は本体の音量/イヤホンをご利用ください)");
      return;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outs = devices.filter((d) => d.kind === "audiooutput");
      const ear = outs.find((d) => /earpiece|receiver|受話|handset/i.test(d.label));
      const onSpeaker = !audio.sinkId || audio.sinkId === "" || audio.sinkId === "default";
      if (onSpeaker && ear) {
        await audio.setSinkId(ear.deviceId);
        btn.dataset.mode = "ear";
        const lbl = btn.querySelector("span"); if (lbl) lbl.textContent = "受話口";
        const ic = btn.querySelector("i"); if (ic) ic.className = "fa-solid fa-phone";
        setStatus("📞 電話モード (耳に当てて聴く)");
      } else if (!onSpeaker) {
        await audio.setSinkId("default");
        btn.dataset.mode = "speaker";
        const lbl = btn.querySelector("span"); if (lbl) lbl.textContent = "スピーカー";
        const ic = btn.querySelector("i"); if (ic) ic.className = "fa-solid fa-volume-high";
        setStatus("🔊 スピーカーで再生中");
      } else {
        setStatus("この端末では受話口デバイスが見つかりません");
      }
    } catch (_e) {
      setStatus("出力の切替に失敗しました");
    }
  }

  // リスナー: タップで音声再生を確実化 (autoplay ブロック対策)。
  enableAudio(): void {
    if (this.audioCtx && this.audioCtx.state === "suspended") { void this.audioCtx.resume(); }
    const audio = document.getElementById("oshaberiRemoteAudio") as HTMLAudioElement | null;
    if (!audio) return;
    const p = audio.play();
    if (p && p.catch) p.catch(() => {});
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    if (this.pollAbort) this.pollAbort.abort();
    this.meterStops.forEach((stopMeter) => stopMeter());
    this.meterStops.clear();
    if (this.audioCtx) { try { void this.audioCtx.close(); } catch (_e) {} this.audioCtx = null; }
    this.micSource = null;
    this.peerSources.clear();
    this.peerDests.clear();
    this.pcs.forEach((pc) => { try { pc.close(); } catch (_e) {} });
    this.pcs.clear();
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }
  }

  private async fetchIceServers(): Promise<void> {
    try {
      const r = await fetch(urlPrefix() + "/oshaberi/" + this.ds.roomId + "/signal/iceservers", {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!r.ok) return;
      const d = (await r.json()) as IceServerCreds;
      if (d.iceServers && d.iceServers.length > 0) {
        this.iceServers = d.iceServers;
      }
      const hasTurn = this.iceServers.some((s) => {
        const u = s.urls;
        const arr = Array.isArray(u) ? u : [u];
        return arr.some((x) => typeof x === "string" && x.indexOf("turn:") === 0);
      });
      dbg("ICE servers=" + this.iceServers.length + " TURN=" + (hasTurn ? "あり" : "なし(STUNのみ)"));
    } catch (_e) {
      // STUN-only にフォールバック
    }
  }

  private async bootstrapExistingPeers(): Promise<void> {
    if (!this.ds.iamHost) return;
    const r = await fetch(urlPrefix() + "/oshaberi/" + this.ds.roomId + "/state", {
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!r.ok) return;
    const d = (await r.json()) as { participants?: Array<{ user_id: number; role: string }> };
    if (!d.participants) return;
    for (const p of d.participants) {
      if (p.user_id === this.ds.myId) continue;
      if (p.user_id === this.ds.hostId) continue;
      await this.dialPeer(p.user_id).catch((e) =>
        console.warn("[oshaberi] dial existing peer failed", p.user_id, e),
      );
    }
  }

  private bindMuteButton(): void {
    const btn = document.getElementById("oshaberiMicBtn") as HTMLButtonElement | null;
    if (!btn) return;
    // テンプレは通話/接続開始まで disabled で描画している (開始前に押せる見た目で
    // 実は無反応、という誤解を防ぐ)。ストリームが整ったここで活性化する。
    if (this.localStream) btn.disabled = false;
    // 初期表示をトラックの実状態に合わせる (リスナーは取得直後ミュート済)
    const t0 = this.localStream ? this.localStream.getAudioTracks()[0] : null;
    if (t0) {
      btn.setAttribute("aria-pressed", t0.enabled ? "false" : "true");
      const l0 = btn.querySelector("span");
      if (l0) l0.textContent = t0.enabled ? "ミュート" : "ミュート解除";
      const i0 = btn.querySelector("i");
      if (i0) i0.className = t0.enabled ? "fa-solid fa-microphone" : "fa-solid fa-microphone-slash";
    }
    btn.addEventListener("click", () => {
      if (!this.localStream) return;
      const track = this.localStream.getAudioTracks()[0];
      if (!track) return;
      const willMute = track.enabled; // 押す前が enabled=true なら mute に
      track.enabled = !willMute;
      btn.setAttribute("aria-pressed", willMute ? "true" : "false");
      const label = btn.querySelector("span");
      if (label) label.textContent = willMute ? "ミュート解除" : "ミュート";
      const ic = btn.querySelector("i");
      if (ic) ic.className = willMute ? "fa-solid fa-microphone-slash" : "fa-solid fa-microphone";
      // サーバ側 is_muted を更新 (UI 同期のため)。失敗は無視。
      fetch(urlPrefix() + "/oshaberi/" + this.ds.roomId + "/" + (willMute ? "mute" : "unmute"), {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken() },
      }).catch(() => {});
    });
  }

  private startPolling(): void {
    if (this.polling) return;
    this.polling = true;
    void this.pollLoop();
  }

  // long-poll ループ: サーバがメッセージ到着まで最大 wait 秒待って返すので、
  // offer/answer/ICE を到着の瞬間に処理できる (旧実装は 1.2 秒間隔ポーリングで、
  // シグナリング 1 往復ごとに最大 1.2 秒の待ちが乗り、参加まで数秒かかっていた)。
  private async pollLoop(): Promise<void> {
    while (!this.stopped) {
      // リスナーで未接続の間は hello=1 を付け、ホストに offer の (再) 送を促す。
      // ホスト先行リロード / offer 取りこぼし / 自分が先に入室、の全てを自己修復
      // するため、待機も短く切って hello を数秒おきに再送する。
      const joining = !this.ds.iamHost && !this.anyConnected;
      const wait = joining ? LONGPOLL_WAIT_JOINING_S : LONGPOLL_WAIT_S;
      const hello = joining ? "&hello=1" : "";
      const ac = new AbortController();
      this.pollAbort = ac;
      try {
        const r = await fetch(
          urlPrefix() + "/oshaberi/" + this.ds.roomId + "/signal/poll?wait=" + wait + hello,
          { credentials: "same-origin", cache: "no-store", signal: ac.signal },
        );
        if (!r.ok) {
          // Promise.withResolvers は iOS Safari 17.4 未満に無いため executor 形式
          await new Promise((resolve) => window.setTimeout(resolve, 3000));
          continue;
        }
        const d = (await r.json()) as { messages?: SignalMsg[] };
        for (const m of d.messages || []) {
          await this.handle(m).catch((e) => console.warn("[oshaberi] handle", m.kind, e));
        }
      } catch {
        if (this.stopped) return;
        // abort (待機モード切替) は即続行、ネットワーク断は少し待って再試行
        if (!ac.signal.aborted) {
          await new Promise((resolve) => window.setTimeout(resolve, 3000));
        }
      } finally {
        if (this.pollAbort === ac) this.pollAbort = null;
      }
    }
  }

  private async handle(m: SignalMsg): Promise<void> {
    dbg("poll受信: " + m.kind + (m.peer ? " peer=" + m.peer : "") + (m.from ? " from=" + m.from : ""));
    switch (m.kind) {
      case "peer_joined":
        if (this.ds.iamHost && m.peer) await this.dialPeer(m.peer);
        return;

      case "peer_left":
        if (m.peer) this.closePeer(m.peer);
        return;

      case "room_ended":
        setStatus("ホストが部屋を閉じました");
        this.stop();
        window.setTimeout(() => {
          window.location.href = urlPrefix() + "/oshaberi";
        }, 1500);
        return;

      case "reaction":
        if (m.emoji) floatEmoji(m.emoji);
        return;

      case "offer":
        if (!m.from || !m.sdp) return;
        await this.onOffer(m.from, m.sdp);
        return;

      case "answer":
        if (!m.from || !m.sdp) return;
        await this.onAnswer(m.from, m.sdp);
        return;

      case "ice":
        if (!m.from || !m.candidate) return;
        await this.onIce(m.from, m.candidate);
        return;
    }
  }

  private newPC(peerID: number): RTCPeerConnection {
    // iceCandidatePoolSize: offer/answer 前に ICE 候補を先行収集して接続確立を速める
    const pc = new RTCPeerConnection({ iceServers: this.iceServers, iceCandidatePoolSize: 4 });
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        void this.postSignal("ice", peerID, undefined, e.candidate.toJSON());
      }
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      dbg("pc[" + peerID + "] ICE: " + st);
      if (st === "checking") setStatus("接続しています…");
      else if (st === "connected" || st === "completed") {
        this.anyConnected = true;
        setStatus(this.ds.iamHost ? "🔴 通話中 (ホスト)" : "🟣 通話中 — ミュート解除で話せます");
      } else if (st === "failed") {
        setStatus("接続に失敗しました。再接続しています…");
        // ホスト: 壊れた PC を捨てて 1.2 秒後に張り直す (リレー経由で再試行)
        if (this.ds.iamHost) {
          this.closePeer(peerID);
          window.setTimeout(() => { void this.dialPeer(peerID); }, 1200);
        } else {
          // リスナー: PC を捨てて hello 再要求モードに戻す。進行中の long-poll
          // (最大 25 秒待機) は中断し、即座に短い待機 + hello 再要求へ切り替える。
          this.closePeer(peerID);
          this.anyConnected = false;
          if (this.pollAbort) this.pollAbort.abort();
        }
      } else if (st === "disconnected") {
        setStatus("接続が不安定です。再接続を待っています…");
      }
    };
    pc.onconnectionstatechange = () => {
      dbg("pc[" + peerID + "] 接続: " + pc.connectionState);
      // リングの点灯は音量メーター (attachLevelMeter) に一本化する。
      // 「connected で点灯」させると、トラックを送らない相手 (マイク拒否の
      // リスナー等) のリングが実音声ゼロのまま永久に光り続ける。
      if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
        this.markSpeaking(peerID, false);
      }
    };
    pc.ontrack = (e) => {
      dbg("pc[" + peerID + "] ontrack ← 音声トラック受信 streams=" + e.streams.length);
      // 受信ジッタバッファの目標を最小化 (Chrome 系)。ミックス転送は
      // 「ホスト受信 + リスナー受信」の 2 段のバッファが乗るため、ここを
      // 絞ると体感遅延が大きく下がる。特にホスト側は全リスナーへの転送
      // 遅延に直結する。回線が悪い時は NetEq が自動で積み増すので安全。
      // lib.dom の RTCRtpReceiver は jitterBufferTarget を必須で持つため、交差型の
      // まま in で絞ると else 側が never に潰れる。実行時の分岐は変えずに、任意
      // プロパティだけの緩い型として扱う。
      const rcv = e.receiver as { jitterBufferTarget?: number | null; playoutDelayHint?: number };
      try {
        if ("jitterBufferTarget" in rcv) rcv.jitterBufferTarget = 0; // ミリ秒
        else if ("playoutDelayHint" in rcv) rcv.playoutDelayHint = 0; // 旧 Chrome (秒)
      } catch { /* 未対応ブラウザは無視 */ }
      // host 側: リスナーの声をローカル再生しつつ、ミキサーへ登録して他の
      // リスナーへ転送する (addPeerToMix)。audio 要素での再生は「聞く」ため
      // だけでなく、Safari で remote stream を WebAudio に食わせると無音になる
      // 既知バグの回避 (要素にアタッチしないと source が流れない) も兼ねる。
      if (this.ds.iamHost) {
        if (e.streams[0]) {
          let a = document.getElementById("ospPeerAudio-" + peerID) as HTMLAudioElement | null;
          if (!a) {
            a = document.createElement("audio");
            a.id = "ospPeerAudio-" + peerID;
            a.autoplay = true;
            (a as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
            document.body.appendChild(a);
          }
          a.srcObject = e.streams[0];
          this.attachLevelMeter(e.streams[0], peerID); // 発言中リスナーの席も光らせる
          this.addPeerToMix(peerID, e.streams[0]); // 他リスナー宛 mix-minus への配線
          const hp = a.play();
          if (hp && hp.catch) hp.catch(() => {});
        }
        return;
      }
      // listener 側: host からの音声を <audio> で再生
      if (!this.ds.iamHost) {
        const audio = document.getElementById("oshaberiRemoteAudio") as HTMLAudioElement | null;
        if (audio && e.streams[0]) {
          audio.srcObject = e.streams[0];
          this.attachLevelMeter(e.streams[0], peerID); // 受信ミックスの音量を可視化 (host 席が全体の声で光る)
          const p = audio.play();
          if (p && p.catch) {
            p.catch(() => {
              setStatus("🔊「音声をオンにする」をタップしてください");
              const btn = document.getElementById("oshaberiEnable") as HTMLButtonElement | null;
              // disabled のまま再表示すると「タップして」と言いながら押せない
              // デッドエンドになる。started 済みなので再タップは enableAudio() のみ。
              if (btn) { btn.disabled = false; btn.hidden = false; }
            });
          }
        }
      }
    };
    return pc;
  }

  // host: 新リスナーを発見 → offer を送る
  private async dialPeer(peerID: number): Promise<void> {
    if (!this.ds.iamHost) return;
    if (!this.localStream) return;
    // 既存 PC が「生きている」なら二重 dial しない。bootstrap と peer_joined が
    // 同じ listener を二度呼ぶと、offer を2回送って SDP/ICE が食い違い接続失敗
    // (= リスナーに音が届かない) の原因になる。failed/closed のときだけ張り直す。
    const existing = this.pcs.get(peerID);
    if (existing) {
      const st = existing.connectionState;
      if (st !== "failed" && st !== "closed" && st !== "disconnected") {
        dbg("host: peer=" + peerID + " は接続中(" + st + ") → 二重dial回避");
        return;
      }
      try { existing.close(); } catch (_e) {}
      this.pcs.delete(peerID);
      this.pendingIce.delete(peerID);
    }
    const pc = this.newPC(peerID);
    this.pcs.set(peerID, pc);
    // 送るのは mix-minus 出力 (ホストのマイク + このリスナー以外の全リスナーの合成)。
    // WebAudio が使えない環境だけ素のマイクを送る縮退 (= ホストの声しか届かない)。
    const dest = this.mixDestFor(peerID);
    const out = dest ? dest.stream : this.localStream;
    if (!out) return;
    out.getTracks().forEach((t) => { pc.addTrack(t, out); });
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await this.postSignal("offer", peerID, pc.localDescription || undefined);
    dbg("host: peer=" + peerID + " へ offer 送信");
  }

  // listener: host からの offer を受けて answer を返す
  private async onOffer(fromID: number, sdp: RTCSessionDescriptionInit): Promise<void> {
    if (this.ds.iamHost) return;
    // 既存 PC があれば閉じる (host 再読込時の再ネゴ等)
    const existing = this.pcs.get(fromID);
    if (existing) {
      try { existing.close(); } catch (_e) {}
      this.pcs.delete(fromID);
    }
    const pc = this.newPC(fromID);
    this.pcs.set(fromID, pc);
    // DM 通話と同じ双方向: 自分の (ミュート済) トラックを載せる。
    // マイクが取れなかった環境では明示の recvonly transceiver で受信を確実化。
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => {
        if (this.localStream) pc.addTrack(t, this.localStream);
      });
    } else if (typeof pc.addTransceiver === "function") {
      try { pc.addTransceiver("audio", { direction: "recvonly" }); } catch (_e) { /* noop */ }
    }
    await pc.setRemoteDescription(sdp);
    await this.flushPendingIce(fromID);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await this.postSignal("answer", fromID, pc.localDescription || undefined);
    dbg("listener: host=" + fromID + " へ answer 送信");
  }

  // host: listener からの answer を受け取る
  private async onAnswer(fromID: number, sdp: RTCSessionDescriptionInit): Promise<void> {
    const pc = this.pcs.get(fromID);
    if (!pc) { dbg("host: answer来たが pc[" + fromID + "] 無し"); return; }
    if (pc.currentRemoteDescription) { dbg("host: answer重複 (無視) peer=" + fromID); return; }
    try {
      await pc.setRemoteDescription(sdp);
      dbg("host: answer適用OK peer=" + fromID);
    } catch (e) {
      dbg("host: answer適用エラー peer=" + fromID + " " + (e && (e as Error).message ? (e as Error).message : ""));
    }
    await this.flushPendingIce(fromID);
  }

  // 両方: ICE candidate を反映
  private async onIce(fromID: number, cand: RTCIceCandidateInit): Promise<void> {
    const pc = this.pcs.get(fromID);
    if (!pc || !pc.remoteDescription) {
      const q = this.pendingIce.get(fromID) || [];
      q.push(cand);
      this.pendingIce.set(fromID, q);
      return;
    }
    try {
      await pc.addIceCandidate(cand);
    } catch (e) {
      console.warn("[oshaberi] addIceCandidate failed", e);
    }
  }

  private async flushPendingIce(peerID: number): Promise<void> {
    const pc = this.pcs.get(peerID);
    if (!pc) return;
    const q = this.pendingIce.get(peerID);
    if (!q) return;
    this.pendingIce.delete(peerID);
    for (const c of q) {
      try { await pc.addIceCandidate(c); } catch (_e) {}
    }
  }

  private closePeer(peerID: number): void {
    const pc = this.pcs.get(peerID);
    if (pc) {
      try { pc.close(); } catch (_e) {}
      this.pcs.delete(peerID);
    }
    this.pendingIce.delete(peerID);
    this.dropMixPeer(peerID);
    const stopMeter = this.meterStops.get(peerID);
    if (stopMeter) {
      stopMeter();
      this.meterStops.delete(peerID);
    }
    this.markSpeaking(peerID, false);
  }

  private markSpeaking(uid: number, on: boolean): void {
    const ring = document.querySelector('.osp-seat[data-user-id="' + uid + '"] .osp-ring');
    if (ring) (ring as HTMLElement).dataset.speaking = on ? "1" : "0";
  }

  // === ホスト側ミキサー (mix-minus) ===
  // スター型トポロジのまま「全員が互いに話す」を実現する: ホストが各リスナーの
  // 受信音声を WebAudio で合成し、リスナー i には「ホストのマイク + i 以外の
  // 全リスナー」のミックスを送る。自分の声を返すと遅延エコーになるため、
  // 宛先ごとに本人だけを除いた合成 (mix-minus) を作る。リスナー側は従来どおり
  // 1 本の受信ストリームを再生するだけで全員の声が聞こえ、端末負荷も変わらない。
  // 音声グラフ: micSource ─┬→ dest(A) ← source(B), source(C), …
  //                        └→ dest(B) ← source(A), source(C), …
  // source は dest にのみ接続し ctx.destination には繋がないので、ホスト自身の
  // 再生 (per-peer audio 要素) と二重になることはない。

  private ensureAudioCtx(): AudioContext | null {
    const AC = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctor = AC.AudioContext || AC.webkitAudioContext;
    if (!Ctor) return null;
    if (!this.audioCtx) this.audioCtx = new Ctor();
    if (this.audioCtx.state === "suspended") { void this.audioCtx.resume(); }
    return this.audioCtx;
  }

  // dialPeer から offer 前に呼ぶ: peerID へ送る mix-minus 出力ノードを作る。
  private mixDestFor(peerID: number): MediaStreamAudioDestinationNode | null {
    if (!this.localStream) return null;
    const ctx = this.ensureAudioCtx();
    if (!ctx) return null;
    // 再 dial では旧ノードを捨てて作り直す (旧 PC と一緒に死んでいる)
    if (this.peerDests.has(peerID)) this.dropMixPeer(peerID);
    const dest = ctx.createMediaStreamDestination();
    if (!this.micSource) this.micSource = ctx.createMediaStreamSource(this.localStream);
    this.micSource.connect(dest);
    this.peerSources.forEach((src, uid) => {
      if (uid !== peerID) src.connect(dest);
    });
    this.peerDests.set(peerID, dest);
    return dest;
  }

  // ontrack (host) から呼ぶ: リスナー peerID の受信音声をミキサーへ登録し、
  // 既存の全宛先 (本人以外) に配線する。再接続では旧 source を張り替える。
  private addPeerToMix(peerID: number, stream: MediaStream): void {
    const ctx = this.ensureAudioCtx();
    if (!ctx) return;
    const old = this.peerSources.get(peerID);
    if (old) { try { old.disconnect(); } catch { /* 未接続なら無視 */ } }
    const src = ctx.createMediaStreamSource(stream);
    this.peerSources.set(peerID, src);
    this.peerDests.forEach((dest, uid) => {
      if (uid !== peerID) src.connect(dest);
    });
  }

  // closePeer から呼ぶ: 離脱したリスナーの source と宛先 dest をグラフから外す。
  private dropMixPeer(peerID: number): void {
    const src = this.peerSources.get(peerID);
    if (src) {
      try { src.disconnect(); } catch { /* 未接続なら無視 */ }
      this.peerSources.delete(peerID);
    }
    const dest = this.peerDests.get(peerID);
    if (dest) {
      // dest への入力エッジは出力側 (mic / 各 source) から切る
      if (this.micSource) { try { this.micSource.disconnect(dest); } catch { /* 同上 */ } }
      this.peerSources.forEach((s) => { try { s.disconnect(dest); } catch { /* 同上 */ } });
      this.peerDests.delete(peerID);
    }
  }

  // 音量メーター: stream の音声レベルを解析し、seatUid の席のリングを実音量で
  // 光らせる/拡大する。ホストは自分のマイク、リスナーは受信音声を可視化できる
  // ので「ちゃんと音が入っているか」が一目で分かる。
  private attachLevelMeter(stream: MediaStream, seatUid: number): void {
    try {
      // 同じ席への張り直し (再接続等) では旧ループを先に止める
      const prev = this.meterStops.get(seatUid);
      if (prev) prev();

      const ctx = this.ensureAudioCtx();
      if (!ctx) return;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser); // destination には繋がない (モニタのみ・エコー無し)
      const data = new Uint8Array(analyser.frequencyBinCount);

      let alive = true;
      let raf = 0;
      const findRing = (): HTMLElement | null =>
        document.querySelector('.osp-seat[data-user-id="' + seatUid + '"] .osp-ring') as HTMLElement | null;
      this.meterStops.set(seatUid, () => {
        alive = false;
        if (raf) cancelAnimationFrame(raf);
        try { src.disconnect(); } catch (_e) {}
        try { analyser.disconnect(); } catch (_e) {}
        const r = findRing();
        if (r) { r.dataset.speaking = "0"; r.style.removeProperty("--lvl"); }
      });

      // ring はグリッドのライブ更新 (state poll) で席ノードごと入れ替わる可能性が
      // あるため、参照が DOM から外れたら引き直す。
      let ring = findRing();
      const tick = (): void => {
        if (!alive || this.stopped) return;
        if (!ring || !ring.isConnected) ring = findRing();
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        const level = Math.min(1, sum / data.length / 80); // 0..1
        if (ring) {
          ring.style.setProperty("--lvl", level.toFixed(2));
          ring.dataset.speaking = level > 0.06 ? "1" : "0";
        }
        raf = requestAnimationFrame(tick);
      };
      tick();
    } catch (_e) { /* noop */ }
  }

  private async postSignal(
    action: "offer" | "answer" | "ice",
    peerID: number,
    sdp?: RTCSessionDescriptionInit | RTCSessionDescription,
    candidate?: RTCIceCandidateInit,
  ): Promise<void> {
    const body: Record<string, unknown> = { peer_id: peerID };
    if (sdp) body.sdp = sdp;
    if (candidate) body.candidate = candidate;
    await fetch(urlPrefix() + "/oshaberi/" + this.ds.roomId + "/signal/" + action, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify(body),
    }).catch(() => {});
  }
}

// ======================================================================
// ここから下: 部屋画面の X Spaces 風 UI (音声接続とは独立して動く部品)
// ======================================================================

// 経過時間タイマー: data-started-at ("YYYY-MM-DD HH:MM:SS" ローカル時刻) から
// #ospTimerVal に mm:ss / h:mm:ss を刻む。
function startElapsedTimer(el: HTMLElement): void {
  const out = document.getElementById("ospTimerVal");
  if (!out) return;
  // epoch 秒 (サーバ計算、閲覧端末の TZ に依存しない) を優先。
  // 文字列 fallback はサーバと同一 TZ の端末でのみ正しい。
  const epoch = parseInt(el.dataset.startedEpoch || "0", 10);
  let started: Date;
  if (epoch > 0) {
    started = new Date(epoch * 1000);
  } else {
    const raw = el.dataset.startedAt || "";
    if (!raw) return;
    started = new Date(raw.replace(" ", "T"));
  }
  if (isNaN(started.getTime())) return;
  const pad = (n: number): string => (n < 10 ? "0" + n : String(n));
  const tick = (): void => {
    const sec = Math.max(0, Math.floor((Date.now() - started.getTime()) / 1000));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    out.textContent = h > 0 ? h + ":" + pad(m) + ":" + pad(s) : m + ":" + pad(s);
  };
  tick();
  window.setInterval(tick, 1000);
}

// 参加者グリッドのライブ更新: /state を 5 秒ごとに poll し、#ospGrid の席を
// 差分更新する。既存席ノードは appendChild で並べ直すだけ (再生成しない) ので、
// 音量メーターが持つ .osp-ring への参照が生きたまま保たれる。
// 部屋が終了したら reload してサーバ描画の終了画面に切り替える。
function startStateUpdater(ds: RoomDataset): void {
  const grid = document.getElementById("ospGrid");
  if (!grid) return;
  let busy = false;
  const tick = async (): Promise<void> => {
    if (busy || document.visibilityState === "hidden") return;
    busy = true;
    try {
      const r = await fetch(urlPrefix() + "/oshaberi/" + ds.roomId + "/state", {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!r.ok) return;
      const d = (await r.json()) as StateResp;
      if (d.status === "ended") {
        // サーバ描画の終了画面に切り替える。参加中で signal poll が動いていれば
        // room_ended 側の誘導と競合するが、どちらが先でも行き先は妥当なので害はない。
        // (参加済みでも「音声をオンにする」を押す前は signal poll が無いので、
        //  この経路が唯一の終了検知になる)
        window.location.reload();
        return;
      }
      const cnt = document.getElementById("ospListenerCount");
      if (cnt && typeof d.listener_count === "number") cnt.textContent = String(d.listener_count);
      if (d.participants) renderSeats(grid, d.participants);
    } catch (_e) {
      // 一時的な失敗は無視
    } finally {
      busy = false;
    }
  };
  window.setInterval(() => { void tick(); }, STATE_INTERVAL_MS);
  void tick();
}

function renderSeats(grid: HTMLElement, parts: StatePart[]): void {
  // 顔ぶれと並び順が前回と同じなら DOM を並べ替えない (5 秒ごとの appendChild は
  // 同一位置でも childList 変異になり、スクリーンリーダーや MutationObserver に
  // 無意味なイベントを流し続けるため)。属性だけ差分更新する。
  const current = Array.from(grid.querySelectorAll(".osp-seat")).map(
    (s) => (s as HTMLElement).dataset.userId || "",
  );
  const wanted = parts.map((p) => String(p.user_id));
  const sameOrder =
    current.length === wanted.length && current.every((v, i) => v === wanted[i]);

  if (sameOrder) {
    for (const p of parts) {
      const seat = grid.querySelector(
        '.osp-seat[data-user-id="' + p.user_id + '"]',
      ) as HTMLElement | null;
      if (seat) updateSeat(seat, p);
    }
    return;
  }

  const seen = new Set<string>();
  for (const p of parts) {
    const key = String(p.user_id);
    seen.add(key);
    let seat = grid.querySelector('.osp-seat[data-user-id="' + key + '"]') as HTMLElement | null;
    if (!seat) {
      seat = buildSeat(p);
    } else {
      updateSeat(seat, p);
    }
    grid.appendChild(seat); // 既存ノードは移動になる (サーバ順に揃える)
  }
  grid.querySelectorAll(".osp-seat").forEach((s) => {
    const uid = (s as HTMLElement).dataset.userId || "";
    if (!seen.has(uid)) s.remove();
  });
}

// テンプレ oshaberi_room.html の席マークアップと同一の DOM を作る。
// テンプレ側を変えたらここも揃えること。
function buildSeat(p: StatePart): HTMLElement {
  const seat = document.createElement("div");
  seat.className = "osp-seat osp-seat--" + p.role;
  seat.dataset.userId = String(p.user_id);

  const av = document.createElement("div");
  av.className = "osp-avatar";
  // 画像を設定していない人は既定の絵 (internal/defavatar)
  const img = document.createElement("img");
  img.src = p.avatar_url || urlPrefix() + "/avatar/default/" + encodeURIComponent(p.handle || "");
  img.alt = "";
  av.appendChild(img);
  const ring = document.createElement("span");
  ring.className = "osp-ring";
  ring.dataset.speaking = "0";
  av.appendChild(ring);
  const mute = document.createElement("span");
  mute.className = "osp-mute";
  mute.innerHTML = '<i class="fa-solid fa-microphone-slash"></i>';
  mute.hidden = !p.is_muted;
  av.appendChild(mute);
  seat.appendChild(av);

  const name = document.createElement("div");
  name.className = "osp-name";
  name.textContent = p.name || p.handle;
  seat.appendChild(name);

  const role = document.createElement("div");
  role.className = "osp-role-badge osp-role-badge--" + p.role;
  if (p.role === "host") {
    role.innerHTML = '<i class="fa-solid fa-microphone-lines"></i> ホスト';
  } else {
    role.textContent = p.role === "speaker" ? "話し手" : "リスナー";
  }
  seat.appendChild(role);
  return seat;
}

function updateSeat(seat: HTMLElement, p: StatePart): void {
  const mute = seat.querySelector(".osp-mute") as HTMLElement | null;
  if (mute) mute.hidden = !p.is_muted;
  const name = seat.querySelector(".osp-name");
  if (name) name.textContent = p.name || p.handle;
}


// 共有ボタン: Web Share API → 失敗/未対応はリンクコピー + トースト。
function bindShareButton(el: HTMLElement): void {
  const btn = document.getElementById("ospShareBtn");
  if (!btn) return;
  btn.addEventListener("click", () => {
    const title = el.dataset.title || "お喋り部屋";
    const url = window.location.href;
    const nav = navigator as Navigator & { share?: (d: { title: string; url: string }) => Promise<void> };
    if (typeof nav.share === "function") {
      nav.share({ title: title + " | お喋り部屋", url }).catch(() => {});
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(
        () => { if (window.showToast) window.showToast("部屋のリンクをコピーしました", "success"); },
        () => { if (window.showToast) window.showToast("コピーに失敗しました", "error"); },
      );
    }
  });
}

// 絵文字リアクション: ピッカー開閉 + 送信 (自分はローカルで即演出、
// 他の参加者には signal 経由で届く)。
function bindReactions(ds: RoomDataset): void {
  const btn = document.getElementById("ospReactBtn");
  const pop = document.getElementById("ospReactPop");
  if (!btn || !pop) return;
  btn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    pop.hidden = !pop.hidden;
  });
  document.addEventListener("click", (ev) => {
    if (!pop.hidden && !pop.contains(ev.target as Node)) pop.hidden = true;
  });
  let lastSent = 0;
  pop.querySelectorAll<HTMLButtonElement>(".osp-react-emoji").forEach((b) => {
    b.addEventListener("click", () => {
      const emoji = b.dataset.emoji || "";
      if (!emoji) return;
      floatEmoji(emoji); // 自分にはすぐ見せる
      const now = Date.now();
      if (now - lastSent < 300) return; // サーバ側の間引きに合わせた連打抑制
      lastSent = now;
      fetch(urlPrefix() + "/oshaberi/" + ds.roomId + "/signal/react", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ emoji }),
      }).catch(() => {});
    });
  });
}

// 画面右下から絵文字がふわっと浮かんで消える。
function floatEmoji(emoji: string): void {
  const layer = document.getElementById("ospFx");
  if (!layer) return;
  if (layer.childElementCount > 40) return; // 大量受信時の DOM 増殖ガード
  const span = document.createElement("span");
  span.className = "osp-fx-item";
  span.textContent = emoji;
  span.style.setProperty("--dx", (Math.random() * 60 - 30).toFixed(0) + "px");
  span.style.setProperty("--dur", (1.8 + Math.random() * 1.2).toFixed(2) + "s");
  layer.appendChild(span);
  window.setTimeout(() => span.remove(), 3200);
}
